import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * EVV (Electronic Visit Verification).
 *
 * Two write paths to this surface:
 *   1. Session-scoped check-in / check-out — the normal RBT flow. Hooks below
 *      post to /api/sessions/:id/check-in and /check-out; the server derives
 *      client/staff identity from the session row so no PHI strings are
 *      supplied by the caller.
 *   2. Admin PATCH on /api/evv-records/:id — reviewer actions (mark
 *      Exception, fix GPS). Not a full admin CRUD — create-via-string is
 *      intentionally excluded from the client.
 *
 * The report-page list uses a GET with filter query params. Filters only
 * carry dates, UUIDs, and enum values — never PHI strings.
 */

export type EvvStatus = 'Open' | 'Pending' | 'Verified' | 'Unverified' | 'Exception';

export interface EvvRecord {
    id: string;
    sessionId: string | null;
    date: string | null;
    client: string;
    staff: string;
    service: string;
    checkIn: string | null;
    checkOut: string | null;
    durMin: number;
    location: string;
    gpsIn: string | null;
    gpsOut: string | null;
    gpsMatch: boolean;
    status: EvvStatus;
    exception: string | null;
}

export interface EvvFilter {
    dateFrom?: string;
    dateTo?: string;
    staffId?: string;
    clientId?: string;
    status?: EvvStatus;
    exceptionOnly?: boolean;
}

export interface CheckInBody {
    lat?: number | null;
    lng?: number | null;
}

function filterToParams(filter: EvvFilter): Record<string, string> {
    const params: Record<string, string> = {};
    if (filter.dateFrom)      params.dateFrom = filter.dateFrom;
    if (filter.dateTo)        params.dateTo = filter.dateTo;
    if (filter.staffId)       params.staffId = filter.staffId;
    if (filter.clientId)      params.clientId = filter.clientId;
    if (filter.status)        params.status = filter.status;
    if (filter.exceptionOnly) params.exceptionOnly = 'true';
    return params;
}

// ── List / filter ─────────────────────────────────────────────

export function useEvvRecords(filter: EvvFilter) {
    return useQuery({
        queryKey: [
            'evv', 'list',
            filter.dateFrom ?? '', filter.dateTo ?? '',
            filter.staffId ?? '', filter.clientId ?? '',
            filter.status ?? '', filter.exceptionOnly ? '1' : '',
        ] as const,
        queryFn: async () => {
            const res = await api.get<EvvRecord[]>('/evv-records', {
                params: filterToParams(filter),
            });
            return res.data;
        },
        staleTime: 30_000,
    });
}

// ── Session-scoped single record (for SessionPage controls) ───

export function useSessionEvv(sessionId: string | undefined) {
    return useQuery({
        queryKey: ['evv', 'session', sessionId ?? ''] as const,
        enabled: !!sessionId,
        queryFn: async () => {
            const res = await api.get<EvvRecord | null>(`/sessions/${sessionId}/evv`);
            return res.data;
        },
    });
}

// ── Check-in / check-out mutations ────────────────────────────

export function useCheckIn(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (body: CheckInBody) => {
            const res = await api.post<EvvRecord>(`/sessions/${sessionId}/check-in`, body);
            return res.data;
        },
        onSuccess: (data) => {
            qc.setQueryData(['evv', 'session', sessionId] as const, data);
        },
        onSettled: () => {
            // Refetch on both success and error so a 409 (already-checked-in
            // race) pulls the real row and converges the UI state.
            qc.invalidateQueries({ queryKey: ['evv', 'session', sessionId] });
            qc.invalidateQueries({ queryKey: ['evv', 'list'] });
        },
    });
}

export function useCheckOut(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (body: CheckInBody) => {
            const res = await api.post<EvvRecord>(`/sessions/${sessionId}/check-out`, body);
            return res.data;
        },
        onSuccess: (data) => {
            qc.setQueryData(['evv', 'session', sessionId] as const, data);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: ['evv', 'session', sessionId] });
            qc.invalidateQueries({ queryKey: ['evv', 'list'] });
        },
    });
}

// ── Reviewer PATCH (status / exception corrections) ───────────

export interface EvvUpdatePayload {
    status?: EvvStatus;
    exception?: string | null;
}

export function useUpdateEvv() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: EvvUpdatePayload }) => {
            const res = await api.patch<EvvRecord>(`/evv-records/${id}`, updates);
            return res.data;
        },
        onSuccess: (data) => {
            if (data.sessionId) {
                qc.setQueryData(['evv', 'session', data.sessionId] as const, data);
            }
            qc.invalidateQueries({ queryKey: ['evv', 'list'] });
        },
    });
}

// ── CSV export ────────────────────────────────────────────────

export function useExportEvv() {
    return useMutation({
        mutationFn: async (filter: EvvFilter): Promise<{ filename: string }> => {
            const res = await api.post('/evv-records/export', filter ?? {}, { responseType: 'blob' });

            const cd = res.headers['content-disposition'] as string | undefined;
            const match = cd && /filename="([^"]+)"/.exec(cd);
            const filename = match ? match[1] : 'evv.csv';

            const objectUrl = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);

            return { filename };
        },
    });
}

/**
 * Reads the browser's current geolocation, bounded by a short timeout so a
 * slow or missing sensor doesn't block check-in. Resolves with null on any
 * failure — caller decides how to surface "no GPS" to the user. The
 * component layer (SessionPage) treats null as "permitted but unavailable"
 * and still allows the check-in to proceed, matching the Unverified-on-deny
 * policy.
 */
export function captureCurrentPosition(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            resolve(null);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 8_000, maximumAge: 60_000 },
        );
    });
}
